import { useCallback, useEffect, useRef, useState } from 'react'

export interface UseSpeechSynthesisResult {
  speak: (text: string) => void
  cancel: () => void
  isSpeaking: boolean
  isSupported: boolean
  voices: SpeechSynthesisVoice[]
  currentVoice: SpeechSynthesisVoice | null
}

const PREFERRED_NAMES = [
  'Microsoft Denise',
  'Microsoft Eloise',
  'Microsoft Brigitte',
  'Microsoft Henri',
  'Amelie',
  'Thomas',
  'Audrey',
]

function pickFrenchVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  if (voices.length === 0) return null
  for (const name of PREFERRED_NAMES) {
    const match = voices.find((v) => v.name.includes(name))
    if (match) return match
  }
  const frFr = voices.find((v) => v.lang === 'fr-FR')
  if (frFr) return frFr
  const frCa = voices.find((v) => v.lang === 'fr-CA')
  if (frCa) return frCa
  const anyFr = voices.find((v) => v.lang.toLowerCase().startsWith('fr'))
  if (anyFr) return anyFr
  return voices[0] ?? null
}

// Markdown is unlikely from Stark but strip the obvious tokens so TTS doesn't
// pronounce "asterisk asterisk bold asterisk asterisk".
function stripMarkdown(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^#+\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim()
}

export function useSpeechSynthesis(): UseSpeechSynthesisResult {
  const synth = typeof window !== 'undefined' ? window.speechSynthesis : null
  const isSupported = synth !== null && typeof SpeechSynthesisUtterance !== 'undefined'

  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([])
  const [isSpeaking, setIsSpeaking] = useState(false)
  const currentVoiceRef = useRef<SpeechSynthesisVoice | null>(null)

  useEffect(() => {
    if (!synth) return undefined
    const refresh = () => {
      const list = synth.getVoices()
      setVoices(list)
      currentVoiceRef.current = pickFrenchVoice(list)
    }
    refresh()
    synth.addEventListener('voiceschanged', refresh)
    return () => {
      synth.removeEventListener('voiceschanged', refresh)
    }
  }, [synth])

  useEffect(() => {
    return () => {
      synth?.cancel()
    }
  }, [synth])

  const speak = useCallback(
    (text: string) => {
      if (!synth) return
      const cleaned = stripMarkdown(text)
      if (!cleaned) return
      synth.cancel()
      const utterance = new SpeechSynthesisUtterance(cleaned)
      const voice = currentVoiceRef.current ?? pickFrenchVoice(synth.getVoices())
      if (voice) {
        utterance.voice = voice
        utterance.lang = voice.lang
      } else {
        utterance.lang = 'fr-FR'
      }
      utterance.rate = 1
      utterance.pitch = 1
      utterance.onstart = () => setIsSpeaking(true)
      utterance.onend = () => setIsSpeaking(false)
      utterance.onerror = () => setIsSpeaking(false)
      synth.speak(utterance)
    },
    [synth],
  )

  const cancel = useCallback(() => {
    synth?.cancel()
    setIsSpeaking(false)
  }, [synth])

  return {
    speak,
    cancel,
    isSpeaking,
    isSupported,
    voices,
    currentVoice: currentVoiceRef.current,
  }
}
